import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ReferAFriendDialog({ businessId, businessName, programActive }) {
  const [eligible, setEligible] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ referee_name: "", referee_email: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!businessId || !programActive) return;
    let cancelled = false;
    api
      .get(`/businesses/${businessId}/referrals/eligibility`)
      .then(({ data }) => { if (!cancelled) setEligible(!!data?.eligible); })
      .catch(() => { if (!cancelled) setEligible(false); });
    return () => { cancelled = true; };
  }, [businessId, programActive]);

  if (!programActive || !eligible) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/businesses/${businessId}/referrals`, form);
      toast.success(`Referral sent to ${form.referee_name}`);
      setOpen(false);
      setForm({ referee_name: "", referee_email: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] h-10 px-5 shadow-sm hover:shadow-md"
        data-testid="refer-a-friend-button"
      >
        <UserPlus size={16} className="mr-2" />Refer a friend
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md rounded-[24px]" data-testid="refer-a-friend-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={18} className="text-[#B31E1E]" />
              Refer a friend to {businessName}
            </DialogTitle>
            <DialogDescription>
              They'll get a welcome offer in their inbox. Once they claim it, we'll send you a thank-you reward.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="ref-name">Friend's name</Label>
              <Input
                id="ref-name"
                required
                value={form.referee_name}
                onChange={(e) => setForm({ ...form, referee_name: e.target.value })}
                placeholder="Jordan"
                className="rounded-md mt-1"
                data-testid="refer-friend-name"
              />
            </div>
            <div>
              <Label htmlFor="ref-email">Friend's email</Label>
              <Input
                id="ref-email"
                type="email"
                required
                value={form.referee_email}
                onChange={(e) => setForm({ ...form, referee_email: e.target.value })}
                placeholder="jordan@example.com"
                className="rounded-md mt-1"
                data-testid="refer-friend-email"
              />
              <p className="text-xs text-gray-500 mt-1">We&apos;ll send the offer once — your friend can opt out from the message itself.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-[35px]">Cancel</Button>
              <Button type="submit" disabled={sending} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="refer-friend-submit">
                {sending ? "Sending…" : "Send referral"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
